import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/utils';

/**
 * A button that reads as a link.
 *
 * Not `<a>`: every use of this triggers an in-app action or a client-side mode
 * swap, not a document fetch, so a real anchor would be a lie to assistive tech
 * and would offer a meaningless "open in new tab".
 *
 * This replaces four hand-rolled recipes that had drifted apart - the auth
 * form's swap link, the status card's secondary action, the "Forgot your
 * password?" link under the password field, and the recovery action inside an
 * alert. They differed in weight, colour and width for no recorded reason.
 *
 * - `emphasis='strong'` is the one that ends a sentence someone is reading
 *   ("New to Xonera? **Create an account**").
 * - `emphasis='muted'` is a standalone secondary action under a primary button.
 */
const textLinkVariants = cva(
  'underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm',
  {
    variants: {
      emphasis: {
        strong: 'font-medium text-foreground',
        muted: 'text-muted-foreground hover:text-foreground',
      },
      size: {
        body: 'text-body',
        label: 'text-label',
      },
      block: { true: 'w-full text-center', false: '' },
    },
    defaultVariants: { emphasis: 'muted', size: 'body', block: false },
  }
);

export interface TextLinkProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof textLinkVariants> {}

export function TextLink({
  className,
  emphasis,
  size,
  block,
  ...props
}: TextLinkProps) {
  return (
    <button
      type='button'
      className={cn(textLinkVariants({ emphasis, size, block }), className)}
      {...props}
    />
  );
}
